import { Fragment, useMemo } from 'react';
import { H4, P, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ControlledFormInput } from '../../Lib/Utils/FormUtils/ControlledFormInput';
import { AsnHeader } from '../../Lib/Api/asn';
import {
  AsnSubmitFormData,
  SHIPMENT_TYPE_OPTIONS,
} from './schemas/asnConfiguration.schema';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('LLL dd, yyyy') : '-';
}

function buildShipmentInfoFields(asn: AsnHeader) {
  return [
    { label: 'ASN Number', value: asn.asn_number },
    { label: 'Carton Count', value: asn.carton_count?.toString() ?? '-' },
    { label: 'Unit Count', value: asn.unit_count?.toString() ?? '-' },
    { label: 'Setup Date', value: formatDate(asn.created_at) },
    { label: 'PO Type', value: asn.po_type ?? '-' },
    { label: 'Vendor Name', value: asn.vendor_name ?? '-' },
    { label: 'Due Date', value: formatDate(asn.po_due_date) },
    {
      label: 'Destination Country',
      value: asn.destination_country ?? '-',
    },
    {
      label: 'Final Destination Location',
      value: asn.final_destination_location ?? '-',
    },
    {
      label: 'Country of Origin',
      value: asn.po_country_of_origin ?? '-',
    },
    { label: 'Shipment Mode', value: asn.po_shipment_mode ?? '-' },
    { label: 'Incoterm', value: asn.po_incoterm ?? '-' },
  ];
}

type ShipmentDetailsProps = {
  asnHeader: AsnHeader;
};

export default function ShipmentDetails({ asnHeader }: ShipmentDetailsProps) {
  const { control, watch } = useFormContext<AsnSubmitFormData>();
  const shipmentType = watch('header.shipment_type');
  const isSealRequired = shipmentType === 'FCL' || shipmentType === 'FTL';

  const infoFields = useMemo(
    () => buildShipmentInfoFields(asnHeader),
    [asnHeader],
  );

  const shipmentTypeSelectOptions = SHIPMENT_TYPE_OPTIONS.map((type) => ({
    label: type,
    value: type,
  }));

  return (
    <Stack
      direction="col"
      gap="md"
      className="w-[429px] h-full bg-neutral-0 rounded-xl border border-neutral-200 p-6 overflow-y-auto"
      data-testid="asn-shipment-details"
    >
      <H4>Shipment Details</H4>
      <div
        className="grid grid-cols-[auto_1fr] gap-y-1 gap-x-4 w-full bg-neutral-100 rounded-lg border border-neutral-200 p-5"
        data-testid="asn-shipment-details-info"
      >
        {infoFields.map(({ label, value }) => (
          <Fragment key={label}>
            <P size="xs" color="disabled">
              {label}
            </P>
            <P
              size="xs"
              color="primary"
              className="min-w-0 truncate"
              title={value}
              data-testid={`asn-info-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {value}
            </P>
          </Fragment>
        ))}
      </div>

      <Stack direction="col" gap="sm" data-testid="asn-shipment-details-form">
        <ControlledFormInput
          name="header.port_of_loading"
          label="Port of Loading"
          control={control}
          inputType="text"
          inputProps={{ placeholder: 'e.g. Shanghai, China (CNSHA)' }}
        />
        <ControlledFormInput
          name="header.port_of_entry"
          label="Port of Entry"
          control={control}
          inputType="text"
          inputProps={{ placeholder: 'e.g. Los Angeles, CA (USLAX)' }}
        />
        <ControlledFormInput
          name="header.shipment_type"
          label="Shipment Type"
          control={control}
          inputType="select"
          required
          inputProps={{
            placeholder: 'Select...',
            options: shipmentTypeSelectOptions,
          }}
        />
        <ControlledFormInput
          name="header.trailer_number"
          label="Trailer/Container"
          control={control}
          inputType="text"
          required
        />
        <ControlledFormInput
          name="header.bol_number"
          label="BOL #"
          control={control}
          inputType="text"
        />
        <ControlledFormInput
          name="header.carrier_name"
          label="Carrier Name"
          control={control}
          inputType="text"
        />
        <ControlledFormInput
          name="header.carrier_scac"
          label="Carrier SCAC"
          control={control}
          inputType="text"
          inputProps={{ placeholder: '2-4 characters' }}
        />
        <ControlledFormInput
          name="header.seal_number"
          label="Seal"
          control={control}
          inputType="text"
          required={isSealRequired}
        />
        <ControlledFormInput
          name="header.ship_date"
          label="Ship Date"
          control={control}
          inputType="date"
          inputProps={{ placeholder: 'Add', containerClassName: 'w-full' }}
        />
      </Stack>
    </Stack>
  );
}
